import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  Button,
  Dropdown,
  Menu,
  Modal,
  Progress,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import moment from "moment";

import {
  JsonlImportClient,
  JsonlImportRecord,
} from "../../api/jsonlImportApi";
import { BusinessUniteContext } from "../layout/Layout";
import ImportExperience from "./ImportExperience";
import JsonlViewerModal from "../jsonlviewer/JsonlViewerModal";
import { JsonlFetchPage } from "../jsonlviewer/jsonlUtils";
import { getTablePagination } from "../../helpers/TablePaginationHelper";

const { Text } = Typography;
const POLLING_INTERVAL = 5000;

interface JsonlImportPageProps {
  titleKey: string;
  descriptionKey: string;
  api: JsonlImportClient;
  requiresBusinessUnit?: boolean;
}

const statusTag = (status: string, t: (key: string) => string) => {
  if (status === "COMPLETED") {
    return (
      <Tag icon={<CheckCircleOutlined />} color="success">
        {t("Common-Completed")}
      </Tag>
    );
  }
  if (status === "PROCESSING") {
    return <Tag color="processing">{t("Common-Processing")}</Tag>;
  }
  if (status === "ERROR") {
    return (
      <Tag icon={<ExclamationCircleOutlined />} color="error">
        {t("Common-Error")}
      </Tag>
    );
  }
  if (status === "CANCELLED") {
    return <Tag>{t("Common-Cancelled")}</Tag>;
  }
  return (
    <Tag icon={<ClockCircleOutlined />}>{t("Common-Pending")}</Tag>
  );
};

const JsonlImportPage: React.FC<JsonlImportPageProps> = ({
  titleKey,
  descriptionKey,
  api,
  requiresBusinessUnit = false,
}) => {
  const { t } = useTranslation();
  const { contextValue: businessUnitSelected } = useContext(BusinessUniteContext);
  const [imports, setImports] = useState<JsonlImportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [autoStart, setAutoStart] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [downloadingFiles, setDownloadingFiles] = useState<Record<string, boolean>>({});
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerProcessId, setViewerProcessId] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState("");

  const businessUnitId = businessUnitSelected
    ? Number(businessUnitSelected)
    : undefined;

  const loadImports = useCallback(
    async (page = 1, size = 10) => {
      setLoading(true);
      try {
        const data = await api.list(page - 1, size);
        setImports(data.content || []);
        setTotalElements(data.totalElements || 0);
      } catch (error) {
        message.error(t("Error-LoadingImports"));
      } finally {
        setLoading(false);
      }
    },
    [api, t]
  );

  useEffect(() => {
    if (businessUnitSelected) {
      loadImports(currentPage, pageSize);
    }
  }, [businessUnitSelected, currentPage, pageSize, loadImports]);

  useEffect(() => {
    const processing = imports.filter((item) => item.status === "PROCESSING");
    if (processing.length === 0) {
      return undefined;
    }
    const interval = setInterval(async () => {
      await Promise.all(
        processing.map(async (item) => {
          try {
            const updated = await api.progress(item.id);
            setImports((prev) =>
              prev.map((row) => (row.id === item.id ? { ...row, ...updated } : row))
            );
          } catch (error) {
            console.error("Error polling import progress", item.id, error);
          }
        })
      );
    }, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [api, imports]);

  const downloadFile = async (
    key: string,
    load: () => Promise<{ downloadUrl: string; filename: string }>
  ) => {
    if (downloadingFiles[key]) return;
    setDownloadingFiles((prev) => ({ ...prev, [key]: true }));
    try {
      const link = await load();
      const response = await fetch(link.downloadUrl);
      if (!response.ok) {
        throw new Error(String(response.status));
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = link.filename;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error(t("Error-DownloadFailed"));
    } finally {
      setDownloadingFiles((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleUpload: UploadProps["onChange"] = (info) => {
    setFileList(info.fileList);
    if (info.file.status === "done") {
      message.success(`${info.file.name} ${t("Common-UploadSuccess")}`);
      setFileList([]);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} ${t("Common-UploadFailed")}`);
    }
  };

  const handleUploadRequest = async (options: {
    file: File;
    onSuccess: (result?: unknown, file?: File) => void;
    onError: (error: unknown) => void;
  }) => {
    if (requiresBusinessUnit && !businessUnitId) {
      message.error(t("Import-BusinessUnitRequired"));
      options.onError(new Error("business unit"));
      return;
    }
    setUploading(true);
    try {
      const response = await api.upload(options.file, autoStart, businessUnitId);
      options.onSuccess(response, options.file);
      setFileList([]);
      loadImports(currentPage, pageSize);
    } catch (error: any) {
      options.onError(error);
      const apiError = error?.response?.data?.apierror;
      message.error(
        apiError?.message_ES ?? apiError?.message ?? t("Error-UploadFailed")
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (processId: string) => {
    Modal.confirm({
      title: t("Common-ConfirmDelete"),
      content: t("Common-DeleteWarning"),
      okText: t("Common-Delete"),
      okType: "danger",
      cancelText: t("Common-Cancel"),
      onOk: async () => {
        try {
          await api.remove(processId);
          message.success(t("Common-Deleted"));
          loadImports(currentPage, pageSize);
        } catch (error) {
          message.error(t("Error-DeleteFailed"));
        }
      },
    });
  };

  const viewerFetchPage = useMemo<JsonlFetchPage | null>(() => {
    if (!viewerProcessId) {
      return null;
    }
    return async ({ page, size, search }) =>
      api.preview(viewerProcessId, page, size, search) as ReturnType<JsonlFetchPage>;
  }, [api, viewerProcessId]);

  const columns: ColumnsType<JsonlImportRecord> = [
    {
      title: t("Common-Filename"),
      dataIndex: "originalFilename",
      ellipsis: true,
    },
    {
      title: t("Common-Status"),
      dataIndex: "status",
      render: (status: string) => statusTag(status, t),
    },
    {
      title: t("Common-Progress"),
      dataIndex: "percentCompleted",
      responsive: ["md"],
      render: (percent: number) => (
        <Progress percent={Math.round(percent || 0)} size="small" />
      ),
    },
    {
      title: t("Common-Correct"),
      dataIndex: "correct",
      responsive: ["lg"],
    },
    {
      title: t("Common-IncorrectRows"),
      dataIndex: "incorrect",
      responsive: ["lg"],
    },
    {
      title: t("Common-Date"),
      dataIndex: "startedAt",
      responsive: ["lg"],
      render: (value: string) =>
        value ? moment(value).format("DD/MM/YYYY HH:mm") : "—",
    },
    {
      title: t("Common-Actions"),
      key: "actions",
      width: 72,
      render: (_value, record) => (
        <Dropdown
          trigger={["click"]}
          overlay={
            <Menu>
              <Menu.Item
                key="preview"
                onClick={() => {
                  setViewerProcessId(record.id);
                  setViewerTitle(
                    `${t("Common-Visualize")}: ${record.originalFilename || record.id}`
                  );
                  setViewerOpen(true);
                }}
              >
                <EyeOutlined style={{ marginRight: 8 }} />
                {t("Common-Visualize")}
              </Menu.Item>
              {record.status === "PENDING" && (
                <Menu.Item
                  key="start"
                  onClick={async () => {
                    await api.start(record.id);
                    loadImports(currentPage, pageSize);
                  }}
                >
                  <PlayCircleOutlined style={{ marginRight: 8 }} />
                  {t("Common-Start")}
                </Menu.Item>
              )}
              {record.status === "PROCESSING" && (
                <Menu.Item
                  key="cancel"
                  onClick={async () => {
                    await api.cancel(record.id);
                    message.success(t("Common-Cancelled"));
                    loadImports(currentPage, pageSize);
                  }}
                >
                  <StopOutlined style={{ marginRight: 8 }} />
                  {t("Common-Cancel")}
                </Menu.Item>
              )}
              <Menu.Item
                key="errors"
                onClick={() =>
                  downloadFile(`errors_${record.id}`, () => api.errorsUrl(record.id))
                }
              >
                {downloadingFiles[`errors_${record.id}`] ? (
                  <Spin size="small" style={{ marginRight: 8 }} />
                ) : (
                  <DownloadOutlined style={{ marginRight: 8 }} />
                )}
                {t("Common-DownloadErrors")}
              </Menu.Item>
              <Menu.Item
                key="original"
                onClick={() =>
                  downloadFile(`original_${record.id}`, () =>
                    api.originalUrl(record.id)
                  )
                }
              >
                <DownloadOutlined style={{ marginRight: 8 }} />
                {t("Common-Download")}
              </Menu.Item>
              <Menu.Item key="delete" danger onClick={() => handleDelete(record.id)}>
                <DeleteOutlined style={{ marginRight: 8 }} />
                {t("Common-Delete")}
              </Menu.Item>
            </Menu>
          }
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <ImportExperience
        title={t(titleKey)}
        instructions={
          <Space direction="vertical" size={4}>
            <Text>{t(descriptionKey)}</Text>
            {requiresBusinessUnit && (
              <Tooltip title={t("Import-BusinessUnitHint")}>
                <Text type="secondary">{t("Import-BusinessUnitHint")}</Text>
              </Tooltip>
            )}
          </Space>
        }
        accept=".jsonl,.zip"
        uploading={uploading}
        fileList={fileList}
        onUploadChange={handleUpload}
        onUploadRequest={handleUploadRequest}
        autoStart={autoStart}
        onAutoStartChange={setAutoStart}
        records={imports}
        loading={loading}
        columns={columns}
        onRefresh={() => loadImports(currentPage, pageSize)}
        renderFeaturedActions={(record) =>
          record.status === "PROCESSING" ? (
            <Button size="small" onClick={() => api.cancel(record.id)}>
              {t("Common-Cancel")}
            </Button>
          ) : null
        }
        onDelete={(record) => handleDelete(record.id)}
        pagination={getTablePagination(t, {
          current: currentPage,
          total: totalElements,
          pageSize,
          showSizeChanger: true,
          format: "i18n",
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        })}
      />
      {viewerFetchPage ? (
        <JsonlViewerModal
          visible={viewerOpen}
          onClose={() => {
            setViewerOpen(false);
            setViewerProcessId(null);
          }}
          title={viewerTitle}
          fetchPage={viewerFetchPage}
        />
      ) : null}
    </>
  );
};

export default JsonlImportPage;
